import { studioTypes } from 'containers/Studio/config/constants';
import type { UUID } from 'interfaces';

// Reusable identifier type for URL builders
export type PathParam = string | number | UUID;

export * from './apis';
/**
 *  SCREEN CONSTANTS
 */

// When to start to remind about password expiration

// Studio left and right bar widths
export const leftBarWidth = '205px';
export const rightBarWidth = '475px';

/**
 *  CHECKBOX REF TYPES
 */

export const REF_REGEX = /{{(\w+\.\w+)}}|{{(\w+)}}/g;

export const DATE_EDITOR_TYPES = {
  DATE_PICKER: 'datePicker',
  DATE_VAR: 'dateVariable',
};

// default assessment terms
export const DEFAULT_ASSESSMENT_TERMS = 'Assessment';
export const ASSESSMENT_TERMS = {
  assessment: 'Assessment',
  assessmentTermPlural: 'Assessments',
  assessmentTermSingular: 'Assessment',
};

export const DOCUMENT_TYPES = {
  FILE: 'file',
  DOCUMENT: 'document',
};

// this is the type for stencil rendering
export enum PageTypes {
  END = 'END',
  EMAIL = 'EMAIL',
  START = 'START',
  YN = 'YN',
  TXT = 'TXT',
  NUM = 'NUM',
  JAVASCRIPT = 'JAVASCRIPT',
  DROP = 'DROP',
  STATEMENT = 'STMT',
  LIST = 'LIST',
  FORM = 'FORM',
  TABLE = 'TBL',
  REPORT = 'REPORT',
  REPORT_DOC = 'REPORT_DOC',
  DOC_REVIEW = 'DOC_REVIEW',
  MERGE = 'MERGE',
  FILTER = 'FILTER',
  COMP = 'COMP',
  DECISION_TABLE = 'DECISION',
  LOOP = 'LP',
  LOOPIN = 'LP_IN',
  LOOPOUT = 'LP_OUT',
  WORKFLOW = 'WORKFLOW',
  WORKFLOW_PARALLEL = 'PARALLEL',
  WORKFLOW_SEQUENCE = 'SEQUENCE',
  TRANSCRIPT = 'TRANSCRIPT',
  DOCUSIGN = 'DOCUSIGN',
  SECTION = 'SECTION',
  DATABASE = 'DATABASE',
  STATUS = 'STATUS',
  LAWVU = 'LAWVU',
  SALESFORCE = 'SALESFORCE',
  XAKIA = 'XAKIA',
  ADOBESIGN = 'ADOBESIGN',
  DOCUSIGN_V2 = 'DOCUSIGN_V2',
  TICKET = 'TICKET',
}

// this is the label that appears in the left sidebar
export const PAGE_NAMES = {
  [PageTypes.START]: 'START',
  [PageTypes.YN]: 'YN',
  [PageTypes.TXT]: 'TXT',
  [PageTypes.NUM]: 'NUM',
  [PageTypes.JAVASCRIPT]: 'JAVASCRIPT',
  [PageTypes.DROP]: 'DROP',
  [PageTypes.STATEMENT]: 'STMT',
  [PageTypes.LIST]: 'LIST',
  [PageTypes.FORM]: 'FORM',
  [PageTypes.TABLE]: 'TABLE',
  [PageTypes.DOC_REVIEW]: 'DOC REVIEW',
  [PageTypes.REPORT]: 'DOC GEN',
  [PageTypes.REPORT_DOC]: 'REPORT_DOC',
  [PageTypes.MERGE]: 'MERGE',
  [PageTypes.FILTER]: 'FILTER',
  [PageTypes.COMP]: 'COMP',
  [PageTypes.DECISION_TABLE]: 'DECISION',
  [PageTypes.LOOP]: 'LOOP',
  [PageTypes.LOOPIN]: 'LP_IN',
  [PageTypes.LOOPOUT]: 'LP_OUT',
  [PageTypes.WORKFLOW]: 'WORKFLOW',
  [PageTypes.WORKFLOW_PARALLEL]: 'PARALLEL',
  [PageTypes.WORKFLOW_SEQUENCE]: 'SEQUENCE',
  [PageTypes.TRANSCRIPT]: 'TRANSCRIPT',
  [PageTypes.DOCUSIGN]: 'E-SIGN',
  [PageTypes.EMAIL]: 'EMAIL',
  [PageTypes.END]: 'END',
  [PageTypes.SECTION]: 'SECTION',
  [PageTypes.DATABASE]: 'DATABASE',
  [PageTypes.STATUS]: 'STATUS',
  [PageTypes.LAWVU]: 'LAWVU',
  [PageTypes.SALESFORCE]: 'SALESFORCE',
  [PageTypes.XAKIA]: 'XAKIA',
  [PageTypes.ADOBESIGN]: 'ADOBESIGN',
  [PageTypes.DOCUSIGN_V2]: 'DOCUSIGN',
  [PageTypes.TICKET]: 'TICKET',
};

// this is the type for stencil rendering
export const SHAPE_TYPES = {
  [PageTypes.START]: 'Start',
  [PageTypes.STATEMENT]: 'Statement',
  [PageTypes.YN]: 'YesNo',
  [PageTypes.TXT]: 'Text',
  [PageTypes.NUM]: 'Number',
  [PageTypes.LIST]: 'List',
  [PageTypes.DROP]: 'Dropdown',
  [PageTypes.FORM]: 'FormBuilder',
  [PageTypes.TABLE]: 'TableBuilder',
  [PageTypes.REPORT]: 'Report',
  [PageTypes.DOC_REVIEW]: 'DocReview',
  [PageTypes.MERGE]: 'Merge',
  [PageTypes.FILTER]: 'Filter',
  [PageTypes.LOOP]: 'Loop',
  [PageTypes.COMP]: 'Comp',
  [PageTypes.DECISION_TABLE]: 'DecisionTable',
  [PageTypes.WORKFLOW]: 'Workflow',
  [PageTypes.WORKFLOW_PARALLEL]: 'WorkflowParallel',
  [PageTypes.WORKFLOW_SEQUENCE]: 'WorkflowSequence',
  [PageTypes.JAVASCRIPT]: 'Custom',
  [PageTypes.TRANSCRIPT]: 'Transcript',
  [PageTypes.DOCUSIGN]: 'Docusign',
  [PageTypes.EMAIL]: 'Email',
  [PageTypes.END]: 'End',
  [PageTypes.SECTION]: 'Section',
  [PageTypes.DATABASE]: 'Database',
  [PageTypes.STATUS]: 'Status',
  [PageTypes.LAWVU]: 'Lawvu',
  [PageTypes.SALESFORCE]: 'Salesforce',
  [PageTypes.XAKIA]: 'Xakia',
  [PageTypes.DOCUSIGN_V2]: 'DocusignV2',
  [PageTypes.ADOBESIGN]: 'Adobesign',
  [PageTypes.TICKET]: 'Ticket',
} as const;

// FIXME CNTR is missing from this list.
export const FIELD_TYPES = {
  BUTTON: 'ACT',
  DROPDOWN: 'SEL',
  LIST: 'LIST',
  NUMBER: 'NUM',
  PARA: 'PARA',
  TEXT: 'TXT',
  RAD: 'RAD',
  DATE: 'DATE',
  SLDR: 'SLDR',
  CBX: 'CBX',
  COMP: 'COMP',
  LOOP_SET: 'LP_SET',
  LOOP_VALUE: 'LP_VAL',
  LOOP_INDEX: 'LP_IND',
  LOOP_SIZE: 'LP_SIZE',
  FILE: 'FILE',
  DOC: 'DOC',
  USERPICKER: 'USER',
  ADOBESIGN_STATUS: 'ADOBESIGN_STATUS',
  DOCUSIGN_STATUS: 'DOCUSIGN_STATUS',
  // Reasoning of this is just due to requirements
  // https://checkboxai.atlassian.net/browse/DEV-11382
  SIGNED_DOC: 'SIGNED_DOC',
  SIGNED_DATE: 'SIGNED_DATE',
  NUM: 'NUM',
  TXT: 'TXT',
};

export const FILE_TYPES = {
  DOC_TEMPLATE: 'template',
  JS: 'javascript',
  END_USER_FILE: 'end_user_file',
  CSV: 'csv file',
  ALL_FILES: 'allFileTypes',
  DOC_FILES: 'documentFiles',
  EDITABLE_DOC_FILES: 'documentFiles',
  IMAGE_FILES: 'imageFiles',
  SPREADSHEET_FILES: 'spreadsheetFiles',
};

export const WORKFLOW_TYPES = {
  APPROVER: 'Approver',
  CHANGE_USER: 'ChangeUser',
  REVIEWER: 'Reviewer',
};

export const CHANGE_USER_TYPES = {
  WORKFLOW: 'WorkflowGroup',
  USER: 'User',
  EXTERNAL_USER: 'ExternalUser',
  USER_LIST: 'UserList',
} as const;

export const USER_PICKER_GROUPS = {
  TEAM: 'Team',
  WORKFLOW: 'WorkflowGroup',
};

export const ROLE_TYPES = {
  ADMIN: 'Administrator',
};

// Metavariables
export const META_VARS = {
  USER_EMAIL: 'current_user_email',
  ASSESSMENT_URL: 'current_assessment_url',
  ASSESSMENT_NAME: 'assessment_name',
  CREATOR: 'creator',
  SENSITIVE: 'sensitive',
  TRANSCRIPT_LINK: 'transcript_link',
  USER_NAME: 'current_user_name',
  CURRENT_DATE: 'today',
};

export const LINK_TYPES = {
  EMAIL: 'email',
  URL: 'url',
};

// There are four potential ways of fetching white labelling configs:
export const WHITE_LABELLING_SOURCES = {
  ACCOUNT_ID: 'accountID',
  DOMAIN: 'domain',
  PUB_TEAM_UUID: 'pubTeamUUID',
  PUB_ASSESSMENT_UUID: 'pubAssessmentUUID',
};

export const isIFrame = window.self !== window.top;

// Property of Minwoo
export const WIDGET_COLORS = [
  '#63DFB7',
  '#F5C572',
  '#82BAEF',
  '#F57D83',
  '#FF9E6E',
  '#999CD3',
  '#D88EA6',
  '#17BEBB',
  '#698AD6',
  '#D8DCFF',
] as const;

// ###############################################################
export const DATE_FORMATS = {
  'DD/MM/YYYY': 'DD/MM/YYYY',
  'MM/DD/YYYY': 'MM/DD/YYYY',
  'YYYY/MM/DD': 'YYYY/MM/DD',
  'D Month YYYY': 'D MMMM YYYY',
  'Month D, YYYY': 'MMMM D, YYYY',
  'D Mmm YYYY': 'D MMM YYYY',
  'Mmm D, YYYY': 'MMM D, YYYY',
};
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
export const DEFAULT_TRANSCRIPT_DATE_FORMAT = 'D MMMM YYYY';

// ##############################################################

export const DEFAULT_EDITOR_CONTENT = '<p><br></p>';

export const DEBOUNCE_TYPING_DEFAULT = 200;

/**
 *  API CONSTANTS
 */

export const API_BASE_URL = '/api/v1';

// ############# Studio APIs ###############
export const GET_URL_STUDIO = (
  solutionID: PathParam,
  solutionVersionID: PathParam,
  appID: PathParam,
  appVersionID: PathParam
) =>
  `/app/solution/${solutionID}/version/${solutionVersionID}/app/${appID}/studio/${appVersionID}/editor`;

export const GET_URL_SOLUTION = (
  solutionUUID: PathParam,
  solutionVersionUUID: PathParam
) => `/app/solution/${solutionUUID}/version/${solutionVersionUUID}/home`;

export const API_GET_STUDIO = (versionUuid: PathParam) =>
  `/module-version/${versionUuid}/studio`;
export const API_SAVE_STUDIO = (
  versionUuid: PathParam,
  pageCount: PathParam,
  fieldCount: PathParam
) =>
  `/module-version/${versionUuid}/page-count/${pageCount}/field-count/${fieldCount}/studio-save`;

export const API_ADD_WORKFLOW_GROUP = (versionID: PathParam) =>
  `/module-version/${versionID}/workflow-groups`;
export const API_DELETE_WORKFLOW_GROUP = (
  wfgId: PathParam,
  versionID: PathParam
) => `/module-version/${versionID}/workflow-groups/${wfgId}`;

// Module APIs
export const API_GET_MODULE_VERSION_START_BLOCK_VARIABLES = (
  moduleVersionId: number
) => `/module-version/${moduleVersionId.toString()}/start-block/field-defs`;

// export const API_CREATE_VERSION = '/module-version';
export const API_GENERATE_INTEGRATION_CODE = (versionID: PathParam) =>
  `/module-version/${versionID}/integrations/code/generate`;
export const API_GET_MODULE_STATUSES = (moduleID: PathParam) =>
  `/modules/${moduleID}/statuses`;
export const API_GET_MODULE_VERSION_STATUSES = (moduleVersionID: PathParam) =>
  `/module-version/${moduleVersionID}/status`;

// Import / Export File API for Solutions & Apps
export const API_EXPORT_APP_FILE = (
  solutionID: PathParam,
  versionID: PathParam,
  appVersionID: PathParam
) =>
  `/solutions/${solutionID}/versions/${versionID}/module-version/${appVersionID}/download`;
export const API_IMPORT_APP_FILE = (
  solutionID: PathParam,
  versionID: PathParam
) => `/solutions/${solutionID}/versions/${versionID}/module/upload`;

export const API_IMPORT_SOLUTION_FILE = () => '/solutions/upload';

export const API_EXPORT_SOLUTION_VERSION_FILE = (
  solutionID: PathParam,
  versionID: PathParam
) => `/solutions/${solutionID}/versions/${versionID}/download`;

// Project execution APIs
export const API_GET_CURRENT_PAGE = (
  assessmentId: PathParam,
  pageUUID?: string
) => `/project/${assessmentId}${pageUUID ? `/page/${pageUUID}` : ''}`;
export const API_GET_NEXT_PAGE = (assessmentId: PathParam) =>
  `/project/${assessmentId}/save`;
export const API_GET_LAST_PAGE = (assessmentId: PathParam) =>
  `/project/${assessmentId}/back`;
export const API_AUTOSAVE = (assessmentID: PathParam) =>
  `/project/${assessmentID}/autosave`;
export const API_REFRESH_ASSESSMENT = (assessmentID: PathParam) =>
  `/project/${assessmentID}/refresh`;

const attachPageUUID = (pageUUID?: string) =>
  pageUUID ? `?pageUUID=${pageUUID}` : '';
export const API_REVERT_TO_PAGE = (assessmentId: PathParam) =>
  `/project/${assessmentId}/revert`;
export const API_GET_CURRENT_TRANSCRIPT = (
  assessmentId: PathParam,
  pageUUID: PathParam
) => `/project/${assessmentId}/transcript/${pageUUID}`;
export const API_GET_TRANSCRIPT_FILE = (
  assessmentId: PathParam,
  pageUUID?: string
) => `/project/${assessmentId}/transcript-file${attachPageUUID(pageUUID)}`;
export const API_GET_TRANSCRIPT_FILE_PUBLIC = (
  assessmentId: PathParam,
  pageUUID?: string
) =>
  `/public/assessment/${assessmentId}/transcript-file${attachPageUUID(
    pageUUID
  )}`;
export const API_GET_UPLOADED_FILES = (
  assessmentId: PathParam,
  pageUUID?: string
) => `/project/${assessmentId}/uploaded-files${attachPageUUID(pageUUID)}`;
export const API_GET_UPLOADED_FILES_PUBLIC = (
  assessmentId: PathParam,
  pageUUID?: string
) =>
  `/public/assessment/${assessmentId}/uploaded-files${attachPageUUID(
    pageUUID
  )}`;
export const API_CHANGE_WORKFLOW_ASSIGNEE = (assessmentId: PathParam) =>
  `project/${assessmentId}/assign`;
export const API_BULK_CHANGE_WORKFLOW_ASSIGNEE = (
  teamId: PathParam,
  widgetId: PathParam
) => `/team/${teamId}/widgets/${widgetId}/assign-owners`;
export const API_SEND_REMINDER = (assessmentId: PathParam) =>
  `project/${assessmentId}/notify`;
export const API_UPDATE_ASSESSMENT_STATUS = (assessmentID: PathParam) =>
  `/project/${assessmentID}/status`;
export const API_BULK_UPDATE_ASSESSMENT_STATUS = (
  teamId: PathParam,
  widgetId: PathParam
) => `/team/${teamId}/widgets/${widgetId}/bulk-update-status`;

export const API_GET_ASSESSMENT = (assessmentId: PathParam) =>
  `/assessment/${assessmentId}`;
export const API_CREATE_PROJECT = (versionId: PathParam) =>
  `/module-version/${versionId}/project`;
export const API_CREATE_PREVIEW = (moduleId: PathParam, versionId: PathParam) =>
  `/apps/${moduleId}/versions/${versionId}/preview`;
export const API_DELETE_PROJECT = (projectId: PathParam) =>
  `/project/${projectId}`;
export const API_BULK_DELETE_PROJECTS = () => `/project`;

export const API_CREATE_ACCOUNT = '/account';
export const API_CREATE_ACCOUNT_AUTH = '/accounts';
export const API_FETCH_ACCOUNTS = '/account/all';
export const API_GET_ACCOUNT_FOR_USAGE = (accountId: PathParam) =>
  `/account/${accountId}/usage`;
export const API_GET_USER = (accountId: PathParam) =>
  `/account/${accountId}/self`;
export const API_UPDATE_ACCOUNT = (accountId: PathParam) =>
  `/account/${accountId}`;
export const API_DELETE_ACCOUNT = (accountId: PathParam) =>
  `account/${accountId}/deactivate`;
export const API_RESTORE_ACCOUNT = (accountId: PathParam) =>
  `account/${accountId}/activate`;
export const API_GET_SESSION_ACCOUNT = () => `/session-account`;
export const API_UPDATE_SESSION_ACCOUNT = () => `/session-account`;
export const API_SET_ASSESSMENT_IS_SENSITIVE = (assessmentId: PathParam) =>
  `/project/${assessmentId}/sensitive`;

/* BPM action APIs */
export const API_CREATE_TEAM_ACTION = (teamId: PathParam, appId: PathParam) =>
  `/team/${teamId}/app/${appId}/actions`;
export const API_FETCH_TEAM_ACTION = (
  teamId: PathParam,
  appId: PathParam,
  actionId: PathParam
) => `/team/${teamId}/app/${appId}/action/${actionId}`;
export const API_FETCH_TEAM_ACTIONS = (teamId: PathParam, appId: PathParam) =>
  `/team/${teamId}/app/${appId}/actions`;
export const API_UPDATE_TEAM_ACTION = (
  teamId: PathParam,
  appId: PathParam,
  actionId: PathParam
) => `team/${teamId}/app/${appId}/action/${actionId}`; // PUT request

export const API_FETCH_TEAM_VARIABLES = (
  teamID: PathParam,
  appVersionID: PathParam
) => `/team/${teamID}/app-version/${appVersionID}/variables`;

export const API_FETCH_USER_TEAM_APP_PERMISSIONS = (
  teamID: PathParam,
  appID: PathParam
) => `team/${teamID}/app/${appID}/permissions`;

export const API_FETCH_ALL_TEAM_WIDGETS = (teamID: PathParam) =>
  `team/${teamID}/widgets`;
export const API_FETCH_TEAM_WIDGETS_DEFS = (
  teamID: PathParam,
  appID: PathParam
) => `team/${teamID}/app/${appID}/widgets`;
export const API_TEAM_WIDGETS_CREATE = (teamID: PathParam, appID: PathParam) =>
  `team/${teamID}/app/${appID}/widget`;
export const API_TEAM_WIDGET_PREVIEW = (teamID: PathParam, appID: PathParam) =>
  `team/${teamID}/app/${appID}/widget-preview`;
export const API_TEAM_WIDGETS_DELETE = (
  widgetID: PathParam,
  widgetVersion: PathParam
) => `/widget/${widgetID}/${widgetVersion}`;
export const API_TEAM_WIDGETS_FETCH_FIELDS = (
  teamID: PathParam,
  appID: PathParam
) => `/team/${teamID}/app/${appID}/widget-fields`;
export const API_TEAM_WIDGETS_UPDATE = (widgetID: PathParam) =>
  `widget/${widgetID}`;
export const API_GET_WIDGET = (widgetID: PathParam) => `widget/${widgetID}`;
export const API_EXECUTE_WIDGET = (widgetID: PathParam) =>
  `widget/${widgetID}/execute`;
export const API_EXECUTE_TEAM_WIDGET = (
  teamID: PathParam,
  widgetID: PathParam
) => `team/${teamID}/widgets/${widgetID}/execute`;
export const API_FETCH_BRANCHES = (teamID: PathParam, appID: PathParam) =>
  `team/${teamID}/app/${appID}/branches`;
export const API_FETCH_TEAM_APP_USERS_PERMISSIONS = (
  teamID: PathParam,
  appID: PathParam
) => `team/${teamID}/app/${appID}/user-permissions`;
export const API_GET_TEAM_APP_NON_MEMBERS = (
  teamID: PathParam,
  appID: PathParam
) => `team/${teamID}/app/${appID}/non-members`;

export const API_EXPORT_WIDGET = (teamID: PathParam, widgetID: PathParam) =>
  `team/${teamID}/widget/${widgetID}/export`;

export const API_GET_TEAM_DASHBOARD = (teamID: PathParam) =>
  `team/${teamID}/dashboards`;
export const API_CREATE_TEAM_DASHBOARD = (teamID: PathParam) =>
  `team/${teamID}/dashboards`;
export const API_UPDATE_TEAM_DASHBOARD = (
  teamID: PathParam,
  dashboardID: PathParam
) => `team/${teamID}/dashboards/${dashboardID}`;
export const API_GET_TEAM_SOLUTIONS = (teamId: PathParam) =>
  `/team/${teamId}/solutions`;

export const API_GET_TEAMS = '/teams';
export const API_GET_OWNER_TEAMS = 'auth/teams/owner';
export const API_FETCH_ROLES = '/user/rpmatrix';
export const API_FETCH_USER_ROLES = '/roles/all';
export const API_CREATE_ROLE = '/role';
export const API_UPDATE_ROLES = '/roles';
export const API_DELETE_ROLE = (roleId: PathParam) => `/role/${roleId}`;

/** Returns interfaces.tsx: `IUser` */
export const API_GET_USERS = () => '/users';
export const API_CREATE_USER = '/users';
export const API_GET_USER_INFO = (userId: PathParam) => `/user/${userId}`;
export const API_GET_USER_INFO_AUTH = (userId: PathParam) => `/users/${userId}`;
export const API_CHANGE_PASSWORD = '/user/change-password';
export const API_UPDATE_USER = (userId: PathParam) => `/users/${userId}`;
export const API_RESET_PASSWORD = (userId: PathParam) =>
  `/users/${userId}/reset-password`;

export const API_GET_ENV_VARS = (accountID: PathParam) =>
  `account/${accountID}/env-vars`;
export const API_CREATE_ENV_VAR = (accountID: PathParam) =>
  `account/${accountID}/env-vars`;
export const API_UPDATE_ENV_VAR = (accountID: PathParam, envVarID: PathParam) =>
  `account/${accountID}/env-vars/${envVarID}`;
export const API_DELETE_ENV_VAR = (accountID: PathParam, envVarID: PathParam) =>
  `account/${accountID}/env-vars/${envVarID}`;

export const API_START_IMPORT_USERS = '/auth/users/validate-import';
export const API_COMPLETE_IMPORT_USERS = '/auth/users/import';

export const API_IMPORT_ASSESSMENTS = '/import-assessment';
export const API_FETCH_IMPORTED_ASSESSMENTS = (
  teamId: PathParam,
  appId: PathParam
) => `/teams/${teamId}/apps/${appId}/imports`;

export const API_GET_COMMENT_ASSET = (s3Id: PathParam) =>
  `${API_BASE_URL}/ticketing/comment-assets/${s3Id}`;
export const API_GET_TICKET_INLINE_ATTACHMENT = (
  ticketId: PathParam,
  s3Id: PathParam
) => `${API_BASE_URL}/ticketing/tickets/${ticketId}/inline-attachments/${s3Id}`;
export const API_UPLOAD_TO_S3 = () => '/public/s3objects';
export const API_PUBLIC_UPLOAD_TO_S3 = () => '/public/public-s3objects';
export const API_REFRESH_TOKEN = '/auth/refresh-token';
export const API_CHECK_USERNAME = '/auth/login/lookup';
export const API_LOGIN = '/auth/login';
/** Url query param indicating that the app is embedded in an iframe. */
export const IS_EMBEDDED_QUERY_PARAM = 'isEmbedded=true';
/** Url query param indicating that the app is being shared. */
export const IS_SHARED_QUERY_PARAM = 'isShared=true';
/** Url query param indicating that debug mode is on */
export const IS_DEBUG_QUERY_PARAM = 'debug=true';
/** Url query param indicating that the app is running an assessment. */
export const IS_IN_ASSESSMENT_RUNTIME = 'isInRuntime=true';
export const API_WIN_LOGIN = () => '/auth/iwa';
export const API_SET_PASSWORD_TOKEN = '/auth/set-password';
export const API_FORGOT_PASSWORD = 'auth/forgot-password';
export const API_CHECK_LOGIN = '/auth/session';
export const API_SIGN_UP = '/auth/sign-up';

/** AI Provider */
export const API_AI_PROVIDER = `/ai/provider`;

/** AI Assistants */
export const API_CREATE_AI_ASSISTANT_LEGAL_FRONTDOOR = `/legal-frontdoor/assistant`;
export const API_GET_AI_ASSISTANT_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}`;
export const API_LIST_AI_ASSISTANTS_LEGAL_FRONTDOOR = `/legal-frontdoor/assistant`;
export const API_UPDATE_AI_ASSISTANT_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}`;
export const API_UPDATE_AI_ASSISTANT_DETAILS_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/details`;
export const API_PUBLISH_AI_ASSISTANT_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/publish`;
export const API_UNPUBLISH_AI_ASSISTANT = (id: UUID) =>
  `/ai/assistant/${id}/unpublish`;
export const API_DELETE_AI_ASSISTANT_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}`;
export const API_GET_AI_ASSISTANT_OVERVIEW_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/overview`;

export const API_MIGRATE_AI_ASSISTANT_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/migrate`;

export const API_LIST_AI_CONVERSATIONS_LEGAL_FRONTDOOR = (assistantId: UUID) =>
  `/legal-frontdoor/assistant/${assistantId}/conversation`;
export const API_EXPORT_AI_ASSISTANT_CONVERSATION_LEGAL_FRONTDOOR = (
  assistantId: UUID
) => `/legal-frontdoor/assistant/${assistantId}/conversation/export`;

export const API_LIST_AI_CONVERSATION_MESSAGES_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  conversationId: UUID
) => `/legal-frontdoor/assistant/${assistantId}/conversation/${conversationId}`;
export const API_SEND_MESSAGE_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  withCitations?: boolean
) =>
  `/legal-frontdoor/assistant/${assistantId}/conversation${
    withCitations ? `?withCitations=true` : ``
  }`;
export const API_SEND_MESSAGE_STREAM_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  withCitations?: boolean
) =>
  `/api/v1/legal-frontdoor/assistant/${assistantId}/conversation/stream${
    withCitations ? `?withCitations=true` : ``
  }`;
export const API_RATE_MESSAGE_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  messageId: UUID
) => `/legal-frontdoor/assistant/${assistantId}/message/${messageId}/rate`;
export const API_CREATE_AGENT_GUIDANCE_LEGAL_FRONTDOOR = (assistantId: UUID) =>
  `/legal-frontdoor/assistant/${assistantId}/agent-guidance`;
export const API_GET_AGENT_GUIDANCE_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  conversationId: UUID
) =>
  `/legal-frontdoor/assistant/${assistantId}/agent-guidance/${conversationId}`;
export const API_GET_AGENT_GUIDANCES_LEGAL_FRONTDOOR = (assistantId: UUID) =>
  `/legal-frontdoor/assistant/${assistantId}/agent-guidance`;
export const API_DELETE_AGENT_GUIDANCE_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  conversationId: UUID
) =>
  `/legal-frontdoor/assistant/${assistantId}/agent-guidance/${conversationId}`;
export const API_UPDATE_AGENT_GUIDANCE_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  conversationId: UUID
) =>
  `/legal-frontdoor/assistant/${assistantId}/agent-guidance/${conversationId}`;

export const API_GET_MESSAGE_ATTACHMENT_S3_URL_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  messageId: UUID,
  attachmentId: UUID
) =>
  `/legal-frontdoor/assistant/${assistantId}/message/${messageId}/attachment/${attachmentId}`;

export const API_GET_AI_SOURCES_LEGAL_FRONTDOOR = (
  entityType: string,
  entityId: UUID
) => `/legal-frontdoor/source/${entityType}/${entityId}`;
export const API_CREATE_AI_SOURCE_LEGAL_FRONTDOOR = `/legal-frontdoor/sources`;
/**
 * Forward-declared for the legal-frontdoor update-source frontend hook
 * (backend handler shipped in #5092, frontend wire-up pending).
 * @public
 */
export const API_UPDATE_AI_SOURCE_LEGAL_FRONTDOOR = (sourceId: UUID) =>
  `/legal-frontdoor/source/${sourceId}`;
export const API_TOGGLE_SOURCE_CITATION_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  sourceId: UUID
) =>
  `/legal-frontdoor/${assistantId}/source/${sourceId}/document/allow-citation`;
export const API_DELETE_AI_SOURCE_LEGAL_FRONTDOOR = (sourceId: UUID) =>
  `/legal-frontdoor/source/${sourceId}`;

export const API_GET_AI_FALLBACK_CREATE_MATTER_LEGAL_FRONTDOOR = (
  assistantId: UUID
) =>
  `/legal-frontdoor/action/fallback-create-matter?aiAssistantAppId=${assistantId}`;

export const API_GET_AI_FALLBACK_CREATE_ASSESSMENT_LEGAL_FRONTDOOR = (
  assistantId: UUID
) =>
  `/legal-frontdoor/action/fallback-create-assessment?aiAssistantAppId=${assistantId}`;

export const API_GET_AI_ACTIONS_LEGAL_FRONTDOOR = (assistantId: UUID) =>
  `/legal-frontdoor/action?aiAssistantAppId=${assistantId}`;

export const API_CREATE_AI_ACTION_LEGAL_FRONTDOOR = `/legal-frontdoor/action`;

export const API_UPDATE_AI_ACTION_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/action/${id}`;

export const API_DELETE_AI_ACTION_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/action/${id}`;

export const API_CREATE_AI_FALLBACK_CREATE_MATTER_LEGAL_FRONTDOOR = `/legal-frontdoor/action/fallback-create-matter`;

export const API_CREATE_AI_FALLBACK_CREATE_ASSESSMENT_LEGAL_FRONTDOOR = `/legal-frontdoor/action/fallback-create-assessment`;

export const API_GET_MESSAGE_CITATION_LEGAL_FRONTDOOR = (
  assistantId: UUID,
  messageId: UUID,
  citationId: UUID
) =>
  `/legal-frontdoor/assistant/${assistantId}/message/${messageId}/citation/${citationId}`;

export const API_GET_AI_ASSISTANT_MEMBERS_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/members`;
export const API_CREATE_AI_ASSISTANT_MEMBERS_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/members`;
export const API_UPDATE_AI_ASSISTANT_MEMBERS_LEGAL_FRONTDOOR = (
  id: UUID,
  membersId: UUID
) => `/legal-frontdoor/assistant/${id}/members/${membersId}`;
export const API_DELETE_AI_ASSISTANT_MEMBERS_LEGAL_FRONTDOOR = (
  id: UUID,
  membersId: UUID
) => `/legal-frontdoor/assistant/${id}/members/${membersId}`;

export const API_GET_AI_ASSISTANT_MEMBERSHIP_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/membership`;

export const API_GET_AI_ASSISTANT_MEMBERS_GROUP_LEGAL_FRONTDOOR = (id: UUID) =>
  `/legal-frontdoor/assistant/${id}/groups`;
export const API_CREATE_AI_ASSISTANT_MEMBERS_GROUP_LEGAL_FRONTDOOR = (
  id: UUID
) => `/legal-frontdoor/assistant/${id}/groups`;
export const API_UPDATE_AI_ASSISTANT_MEMBERS_GROUP_LEGAL_FRONTDOOR = (
  id: UUID,
  membersGroupId: UUID
) => `/legal-frontdoor/assistant/${id}/groups/${membersGroupId}`;
export const API_DELETE_AI_ASSISTANT_MEMBERS_GROUP_LEGAL_FRONTDOOR = (
  id: UUID,
  membersGroupId: UUID
) => `/legal-frontdoor/assistant/${id}/groups/${membersGroupId}`;

/* AI Integrations */
export const API_GET_AI_INTEGRATIONS = (id: string) =>
  `/legal-frontdoor/assistant/${id}/integrations`;

/* AI Email Configuration Integration */
export const API_GET_EMAIL_INTEGRATION = (id: PathParam) =>
  `/legal-frontdoor/assistant/${id}/integrations/email`;
export const API_CREATE_EMAIL_INTEGRATION = (id: PathParam) =>
  `/legal-frontdoor/assistant/${id}/integrations/email`;
export const API_UPDATE_EMAIL_INTEGRATION = (id: PathParam) =>
  `/legal-frontdoor/assistant/${id}/integrations/email`;

/* AI Slack Integration */
export const API_GET_SLACK_INTEGRATION = (aiAssistantId: PathParam) =>
  `/legal-frontdoor/assistant/${aiAssistantId}/integration/slack`;
export const API_CREATE_SLACK_INTEGRATION = (aiAssistantId: PathParam) =>
  `/legal-frontdoor/assistant/${aiAssistantId}/integration/slack`;
export const API_UPDATE_SLACK_INTEGRATION = (configId: PathParam) =>
  `/legal-frontdoor/assistant/integration/slack/${configId}`;
export const API_RESEND_AI_ASSISTANT_EMAIL_INTEGRATION = (id: PathParam) =>
  `/legal-frontdoor/assistant/${id}/integrations/email/resend`;
export const API_VERIFY_EMAIL_AI = (appId: string) =>
  `/legal-frontdoor/assistant/${appId}/integrations/email/validate`;

// AI Ms Teams Integration
export const API_CREATE_MS_TEAMS_INTEGRATION = (id: string) =>
  `/ai/assistant/${id}/integrations/msteams`;
export const API_GET_MS_TEAMS_INTEGRATION = (id: string) =>
  `/ai/assistant/${id}/integrations/msteams`;
export const API_UPDATE_MS_TEAMS_INTEGRATION = (id: string) =>
  `/ai/assistant/${id}/integrations/msteams`;

/* Slack Configuration */
export const API_GET_SLACK_CONFIGURATION = `/public/integrations/slack/configuration`;
export const API_CREATE_SLACK_CONFIGURATION = `/public/integrations/slack/configuration`;
export const API_UPDATE_SLACK_CONFIGURATION = `/public/integrations/slack/configuration`;

/* Slack User Connection */
export const API_GET_SLACK_USER_CONNECTION = `/ticketing/slack/user-connection`;
export const API_DELETE_SLACK_USER_CONNECTION = `/ticketing/slack/user-connection`;

/* Ironclad Integration */
export const API_GET_IRONCLAD_REDIRECT = `/public/integrations/ironclad/oauth`;
export const API_IRONCLAD_OAUTH_EXCHANGE = `/public/integrations/ironclad/authorize`;
export const API_GET_IRONCLAD_CREDENTIALS = `/public/integrations/ironclad/credentials`;
export const API_UPSERT_IRONCLAD_DEFAULT_BOARD = `/public/integrations/ironclad/default-board`;
export const API_GET_IRONCLAD_DEFAULT_BOARD = `/public/integrations/ironclad/default-board`;
export const API_IMPORT_IRONCLAD_WORKFLOWS = `/public/integrations/ironclad/workflows/import`;
export const API_GET_IRONCLAD_WORKFLOW_CONFIG = `/public/integrations/ironclad/workflows/config`;
export const API_GET_IRONCLAD_WORKFLOW_CONFIG_DETAIL = (configId: string) =>
  `/public/integrations/ironclad/workflows/config/${configId}`;
export const API_UPDATE_IRONCLAD_WORKFLOW_CONFIG_SELECTED_BOARD = (
  configId: string
) =>
  `/public/integrations/ironclad/workflows/config/${configId}/selected-board`;
export const API_GET_IRONCLAD_WORKFLOW_DATA = (ticketId: string) =>
  `/public/integrations/ironclad/workflows/data/${ticketId}`;
export const API_GET_IRONCLAD_WORKFLOW_DIFF = `/public/integrations/ironclad/workflows/diff`;
export const API_UPDATE_IRONCLAD_WORKFLOW_DIFF = `/public/integrations/ironclad/workflows/diff`;
export const API_DELETE_IRONCLAD_CREDENTIALS = `/public/integrations/ironclad/credentials`;
/* Ticketing Boards */
export const API_CREATE_INITIAL_BOARD = `/ticketing/boards/onboarding`;
export const API_GET_TICKETING_BOARDS = `/ticketing/boards`;
export const API_GET_TICKETING_USER_BOARDS = `/ticketing/user-boards`;
export const API_GET_TICKETING_BOARD = (id: string) =>
  `/ticketing/boards/${id}`;
export const API_DELETE_TICKETING_BOARD = (id: string) =>
  `/ticketing/boards/${id}`;
export const API_CREATE_BOARD = `/ticketing/boards`;
export const API_UPDATE_BOARD_SETTINGS = (id: string) =>
  `/ticketing/boards/${id}/settings`;
export const API_UPDATE_BOARD_EMAIL_INTEGRATION_CONFIG = (id: string) =>
  `ticketing/boards/${id}/integrations/email`;
export const API_RESEND_BOARD_EMAIL_VERIFICATION = (id: string) =>
  `/ticketing/boards/${id}/integrations/email/resend`;
export const API_BOARD_NOTIFICATION_SETTINGS = (
  id: string,
  settingsType?: string
) =>
  `/ticketing/boards/${id}/notifications/settings${
    settingsType ? `?type=${settingsType}` : ''
  }`;
export const API_BOARD_MEMBERS = (boardId: string, memberId?: string) =>
  `ticketing/boards/${boardId}/members${memberId ? `/${memberId}` : ''}`;
export const API_TICKETING_BOARD_MEMBERSHIP = (boardId: string) =>
  `ticketing/boards/${boardId}/membership`;
export const API_GET_BOARD_VIEW_DETAILS = (boardId: string, viewId: string) =>
  `/ticketing/boards/${boardId}/views/${viewId}/definitions`;

/* Ticketing Board Views */
export const API_GET_TICKETING_BOARD_VIEWS = (boardId: string) =>
  `/ticketing/boards/${boardId}/views`;

export const API_GET_TICKETING_MERGEABLE_TICKETS = (boardId: string) =>
  `/ticketing/boards/${boardId}/tickets/mergeable`;

/* Ticketing Collaborators */
export const API_GET_TICKETING_COLLABORATORS = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/collaborators`;
export const API_ADD_TICKETING_BULK_COLLABORATOR = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/collaborators/bulk`;
export const API_UPDATE_TICKETING_COLLABORATOR = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/collaborators`;
export const API_REMOVE_TICKETING_COLLABORATOR = (
  ticketId: string,
  userId: string
) => `/ticketing/tickets/${ticketId}/collaborators/${userId}`;

/* Ticketing Tickets */
export const API_GET_TICKET_FIELDS = `/ticketing/tickets/fields`;
export const API_GET_TICKET_DETAILS = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/details`;
export const API_GET_TICKET_SENDER_EMAIL = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/sender-email`;
export const API_GET_TICKET_HISTORY = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/history`;
export const API_MOVE_TICKET_BOARD = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/move-to-board`;
export const API_CONVERT_TICKET_LAYOUT = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/convert-ticket-layout`;
export const API_GET_TICKET_DETAILS_PUBLIC = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/details/public`;
export const API_GET_STATUS_HISTORY = (ticketId: string, fieldId: string) =>
  `/ticketing/tickets/${ticketId}/status-history/${fieldId}`;
export const API_UPDATE_TICKET_DETAILS = (boardId: string, ticketId: string) =>
  `/ticketing/boards/${boardId}/tickets/${ticketId}/details`;
export const API_LIST_TICKETS = (boardId: string, viewId: string) =>
  `/ticketing/boards/${boardId}/views/${viewId}/tickets`;
export const API_LIST_TICKETS_PREVIEW = (boardId: string, viewId: string) =>
  `/ticketing/boards/${boardId}/views/${viewId}/tickets/preview`;
export const API_DELETE_TICKETS = (id: string, ids: string) =>
  `/ticketing/boards/${id}/tickets?ids=${ids}`;
export const API_GET_TICKET_FIELDS_BY_LAYOUT = `ticketing/tickets/fields/layouts`;
export const API_GET_TICKET_COLLABORATORS = `ticketing/tickets/collaborators`;
export const API_TICKETING_TICKET_MEMBERSHIP = (ticketId: string) =>
  `ticketing/tickets/${ticketId}/membership`;

export const API_MERGE_TICKETS = `/ticketing/tickets/merge`;
export const API_GET_TICKET_MERGE_REFERENCE = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/merge-reference`;

/* Ticketing Ticket Layouts */
export const API_GET_TICKET_LAYOUTS = `/ticketing/ticket-layouts`;
export const API_GET_TICKET_LAYOUT_DETAILS = (layoutId: string) =>
  `/ticketing/ticket-layouts/${layoutId}`;

/* Ticketing Ticket Thread */
export const API_GET_TICKET_THREADS = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/threads`;
export const API_DELETE_TICKET_THREAD = (ticketId: string, threadId: string) =>
  `/ticketing/tickets/${ticketId}/threads/${threadId}`;

/* Ticketing Ticket Thread Messages */
export const API_CREATE_TICKET_THREAD_MESSAGES = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/thread-messages`;
export const API_REPLY_TICKET_THREAD_MESSAGES = (
  ticketId: string,
  threadId: string
) => `/ticketing/tickets/${ticketId}/thread-messages/${threadId}`;

/* Ticketing Ticket Messages */
export const API_GET_TICKET_MESSAGES = (ticketId: string, threadId: string) =>
  `/ticketing/tickets/${ticketId}/threads/${threadId}/messages`;
export const API_DELETE_TICKET_MESSAGES = (
  ticketId: string,
  messageId: string
) => `/ticketing/tickets/${ticketId}/messages/${messageId}`;

/* Ticketing Ticket Email File */
export const API_DOWNLOAD_TICKET_EMAIL_MESSAGE = (
  ticketId: string,
  messageId: string
) => `/ticketing/tickets/${ticketId}/messages/${messageId}/email/download`;

/* Ticketing Ticket Comments */
export const API_GET_TICKET_COMMENTS = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/comments`;
export const API_CREATE_TICKET_COMMENT = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/comments`;
export const API_UPDATE_TICKET_COMMENT = (
  ticketId: string,
  commentId: string
) => `/ticketing/tickets/${ticketId}/comments/${commentId}`;
export const API_DELETE_TICKET_COMMENT = (
  ticketId: string,
  commentId: string
) => `/ticketing/tickets/${ticketId}/comments/${commentId}`;

/* Ticketing Ticket Attachments */
export const API_GET_TICKET_ATTACHMENTS = (boardId: string, ticketId: string) =>
  `/ticketing/boards/${boardId}/tickets/${ticketId}/attachments`;
export const API_GET_TICKET_ATTACHMENTS_PAGINATED = (
  boardId: string,
  ticketId: string
) => `/ticketing/boards/${boardId}/tickets/${ticketId}/attachments/paginated`;
export const API_HIDE_TICKET_ATTACHMENT = (id: string) =>
  `/ticketing/tickets/attachments/${id}/hide`;
export const API_HIDE_TICKET_ATTACHMENT_FROM_INLINE = (s3Id: string) =>
  `/ticketing/tickets/attachments/${s3Id}/hide-from-inline`;
export const API_GET_TICKET_ATTACHMENT_BUFFER = (
  ticketId: string,
  ticketAttachmentId: string
) => `/ticketing/tickets/${ticketId}/attachments/${ticketAttachmentId}`;

export const API_GET_TICKET_FIELD_STATUS_GROUPS = `/ticketing/tickets/fields/status-groups`;

export const API_GET_TICKETING_CURRENCY_OPTIONS = `/ticketing/tickets/fields/currency-options`;

export const API_MERGE_FIELD_OPTIONS = `/ticketing/tickets/fields/options/merge`;

/* Ticketing Ticket Permission Group */

/* Ticketing Permission V2 */
export const API_PERMISSION_INVALIDATE_CACHE = () =>
  `/ticketing/permissions/cache/invalidate`;

/* Tasks */
export const API_TASKS_BY_CONTEXT = (contextType: string, contextId: string) =>
  `/tasks/context/${contextType}/${contextId}`;
export const API_TASK = (taskId: string) => `/tasks/${taskId}`;
export const API_TASK_TOGGLE = (taskId: string) => `/tasks/${taskId}/toggle`;
export const API_CALENDAR_BY_CONTEXT = (
  contextType: string,
  contextId: string
) => `/tasks/context/${contextType}/${contextId}/calendar`;

/* Calendar Events */
export const API_CALENDAR_EVENTS_BY_CONTEXT = (
  contextType: string,
  contextId: string
) => `/calendar-events/context/${contextType}/${contextId}`;
export const API_CALENDAR_EVENT = (calendarEventId: string) =>
  `/calendar-events/${calendarEventId}`;

/* Ticketing Slack Messages */
export const API_GET_SLACK_WORKSPACE_MEMBERS = `/ticketing/slack/workspace/members`;
export const API_SEND_SLACK_MESSAGE = (ticketId: string) =>
  `/ticketing/tickets/${ticketId}/slack/messages`;
export const API_REPLY_SLACK_MESSAGE = (ticketId: string, threadId: string) =>
  `/ticketing/tickets/${ticketId}/threads/${threadId}/slack/reply`;
export const API_GET_CHANNEL_MEMBERS = (channelId: string) =>
  `/ticketing/slack/channel/${channelId}/members`;
export const API_UPDATE_SLACK_MESSAGE = (ticketId: string, messageId: string) =>
  `/ticketing/tickets/${ticketId}/slack/messages/${messageId}`;

/* Ticketing Admin */
export const API_VERIFY_EMAIL_TICKETING = (boardId: string) =>
  `/ticketing/boards/${boardId}/integrations/email/validate`;

export const API_AI_DOCUMENT_QUERY = (isPublic = false) =>
  isPublic ? `/public/ai/document/query` : `/ai/document/query`;

export const API_CREATE_GROUP = () => `/auth/groups`;
export const API_CREATE_GROUP_USER = (teamId: number, appId: string) =>
  `/team/${teamId}/app/${appId}/group-user`;
export const API_DELETE_GROUP = (groupId: string) => `/auth/groups/${groupId}`;
export const API_DUPLICATE_GROUP = (groupId: string) =>
  `/auth/groups/${groupId}/duplicate`;
export const API_DELETE_USERS_FROM_GROUP = (groupId: string) =>
  `/auth/groups/${groupId}/users`;
export const API_ADD_USERS_TO_GROUP = (groupId: string) =>
  `/auth/groups/${groupId}/users`;
export const API_ADD_ALL_USERS_TO_GROUP = (groupId: string) =>
  `/auth/groups/${groupId}/users/all`;
export const API_ADD_GROUPS_TO_TEAM = (teamId: number) =>
  `/auth/teams/${teamId}/groups`;
export const API_UPDATE_GROUP = (groupId: string) => `/auth/groups/${groupId}`;
export const API_UPDATE_TEAM_GROUP_PERM = (teamId: number) =>
  `/auth/teams/${teamId}/group-perm`;
export const API_GET_GROUPS = () => `/auth/groups`;
export const API_GET_TEAM_GROUPS = (teamId: number) =>
  `/auth/teams/${teamId}/groups`;
export const API_DELETE_GROUPS_FROM_TEAM = (teamId: number) =>
  `/auth/teams/${teamId}/groups`;
export const API_DELETE_GROUP_USER = (
  teamId: number,
  appId: string,
  teamAppMembershipUuid: string
) => `/team/${teamId}/app/${appId}/permissions/${teamAppMembershipUuid}`;
export const API_GET_GROUP_USERS = (groupId: string) =>
  `/auth/groups/${groupId}/users`;
export const API_GET_APP_GROUP_USERS = (teamId: string, appId: string) =>
  `/auth/teams/${teamId}/app/${appId}/groups`;
export const API_VALIDATE_GROUP_NAME = (groupId?: string) =>
  `/auth/groups/validate-group-name${groupId ? `/${groupId}` : ''}`;

export const API_VALIDATE_PUBLIC_SECURITY_CODE = (
  pubAssessmentUuid: PathParam
) => `/public/assessment/${pubAssessmentUuid}/access-check`;
export const API_GET_PUBLIC_TEAM = (pubTeamUuid: PathParam) =>
  `/public/project-team/${pubTeamUuid}`;
export const API_CREATE_PUBLIC_ASSESSMENT = (pubTeamUuid: PathParam) =>
  `/public/project-team/${pubTeamUuid}`;
export const API_GET_PUBLIC_ASSESSMENT = (
  pubAssessmentUUID: PathParam,
  branchUUID?: string
) =>
  `/public/assessment/${pubAssessmentUUID}${
    branchUUID ? `?branchUUID=${branchUUID}` : ''
  }`;
export const API_GET_PUBLIC_PAGE = (
  pubAssessmentUUID: PathParam,
  branchUUID?: string
) =>
  `/public/assessment/${pubAssessmentUUID}/page${
    branchUUID ? `?branchUUID=${branchUUID}` : ''
  }`;
export const API_PUBLIC_NEXT = (pubAssessmentUuid: PathParam) =>
  `/public/assessment/${pubAssessmentUuid}/save`;
export const API_PUBLIC_BACK = (pubAssessmentUuid: PathParam) =>
  `/public/assessment/${pubAssessmentUuid}/back`;
export const API_PUBLIC_AUTOSAVE = (assessmentID: PathParam) =>
  `/public/assessment/${assessmentID}/autosave`;

// think this API is not being used anymore
export const API_ASSESSMENT_WORKFLOW_ACTION = (
  assessmentID: PathParam,
  pageUUID: PathParam,
  action: string
) => `/assessment/${assessmentID}/${pageUUID}/workflow_action?&op=${action}`;

export const API_SEND_ASSESSMENT_LINK = (pubAssessmentUuid: PathParam) =>
  `/public/assessment/${pubAssessmentUuid}/send-link`;
export const API_REVERT_TO_PAGE_PUBLIC = (pubAssessmentUuid: PathParam) =>
  `/public/assessment/${pubAssessmentUuid}/revert`;

export const buildPublicTeamURL = (pubTeamUuid: string) =>
  `${window.location.origin}/pubt/${pubTeamUuid}`;
export const buildPublicAssessmentURL = (
  pubAssessmentUUID: string,
  branchUUID?: string
) =>
  `${window.location.origin}/puba/${pubAssessmentUUID}${
    branchUUID ? `/branch/${branchUUID}` : ''
  }`;

export const API_RESET_SECURITY_CODE = (teamID: PathParam, appID: PathParam) =>
  `/team/${teamID}/app/${appID}/reset-security-code`;
export const API_DISABLE_SECURITY_CODE = (
  teamId: PathParam,
  appId: PathParam
) => `/team/${teamId}/app/${appId}/security-code`;
export const API_FETCH_WORKFLOW_GROUPS = (
  moduleVersionId: PathParam,
  teamId: PathParam
) => `/workflow-groups/${moduleVersionId}/${teamId}`;
export const API_ASSIGN_WORKFLOW_GROUP_USERS = (wfgId: PathParam) =>
  `/workflow-groups/${wfgId}/workflow-group-user-maps`;
export const API_DELETE_WORKFLOW_GROUP_USER = (
  teamId: PathParam,
  mapId: PathParam
) => `/workflow-group-user-maps/${teamId}/${mapId}`;

// URL is the cloudfront distrib that forwards whitelabelled s3 content

// Legacy E-Signature APIs
export const API_FETCH_ESIGNATURE_INFO = 'account/integrations/esignature/all';
export const API_DELETE_ESIGNATURE_CONFIG = (configID: PathParam) =>
  `account/integrations/esignature/${configID}`;

// Docusign APIs
// no CREATE API because Docusign use OAuth, /exchange API
export const API_GET_DOCUSIGN_CONFIGS = 'public/integrations/docusign';
export const API_UPDATE_DOCUSIGN_CONFIG = (id: PathParam) =>
  `public/integrations/docusign/${id}`;

// Adobe Sign APIs
export const API_CREATE_ADOBE_SIGN_CONFIG = 'public/integrations/adobesign';
export const API_GET_ADOBE_SIGN_CONFIGS = 'public/integrations/adobesign';
export const API_UPDATE_ADOBE_SIGN_CONFIG = (id: PathParam) =>
  `public/integrations/adobesign/${id}`;

export const API_SEND_TO_DOCUSIGN_FOR_PUBLIC_ASSESSMENT = (
  assessmentId: PathParam
) => `public/docusign/${assessmentId}/envelope`;
export const API_SEND_TO_DOCUSIGN_FOR_PRIVATE_ASSESSMENT = (
  assessmentId: PathParam
) => `docusign/${assessmentId}/envelope`;

// TODO: need to agree on API - currently not being implemented
export const API_CANCEL_SEND_TO_DOCUSIGN_FOR_PUBLIC_ASSESSMENT = (
  assessmentId: PathParam
) => `public/cancelDocusign/${assessmentId}/envelope`;
export const API_CANCEL_SEND_TO_DOCUSIGN_FOR_PRIVATE_ASSESSMENT = (
  assessmentId: PathParam
) => `cancelDocusign/${assessmentId}/envelope`;

export const API_GET_ESIGN_RECIPIENTS = (
  assessmentID: PathParam,
  statusName: string
) => `integrations/${assessmentID}/esign/${statusName}/recipients`;

export const WIKI_SEARCH = (query: string) =>
  `https://support.checkbox.ai/hc/en-us/search?utf8=%E2%9C%93&query=${query}`;

export const API_GET_DOC = (assessmentId: PathParam, documentId: PathParam) =>
  `project/${assessmentId}/${documentId}/doc`;
export const API_GET_DOCS = (assessmentId: PathParam) =>
  `project/${assessmentId}/docs`;
export const API_GET_DOC_READONLY_EDITOR = (
  assessmentId: PathParam,
  documentId: PathParam
) => `docs/${assessmentId}/${documentId}/onlyoffice/editor/readonly`;

export const API_FETCH_WHITE_LABELLING = () => '/public/white-labelling';
export const API_UPDATE_WHITE_LABELLING = (accountID: PathParam) =>
  `/white-labelling/${accountID}`;
export const API_GET_WHITE_LABELLING_DOMAINS = () => `/white-labelling-domains`;
export const API_GET_WHITE_LABELLING_VERIFIED_DOMAINS = () =>
  `/white-labelling-domains/verified`;
export const API_GET_WHITE_LABELLING_ALL_DOMAINS = () =>
  `/white-labelling-domains/all`;
export const API_GET_WHITE_LABELLING_DOMAIN_DETAIL = (id: string) =>
  `/white-labelling-domains/${id}`;
export const API_CREATE_WHITE_LABELLING_DOMAINS = () =>
  `/white-labelling-domains`;
export const API_VALIDATE_WHITE_LABELLING_DOMAIN = (id: string) =>
  `/white-labelling-domains/${id}/validate`;
export const API_DELETE_WHITE_LABELLING_DOMAIN = (id: string) =>
  `/white-labelling-domains/${id}`;

export const API_EXPORT_AUDIT_LOG = '/audit';

export const API_GET_PARTNER_API_KEYS = (accountRef: string) =>
  `/auth/partner-api-keys?account_ref=${encodeURIComponent(accountRef)}`;
export const API_GET_PARTNER_API_KEYS_SUITABLE_USERS = (accountRef: string) =>
  `/auth/partner-api-keys/suitable-users?account_ref=${encodeURIComponent(
    accountRef
  )}`;
export const API_POST_PARTNER_API_KEY = () => '/auth/partner-api-keys';
export const API_DELETE_PARTNER_API_KEY = (uuid: UUID, accountRef: string) =>
  `/auth/partner-api-keys/${uuid}?account_ref=${encodeURIComponent(
    accountRef
  )}`;

export const API_GET_APP_EXPOSED_VARS = (
  solutionVersionID: PathParam,
  appID: PathParam
) => `solution-version/${solutionVersionID}/app/${appID}/exposed-variables`;
export const API_LOOKUP_APP_VARS = (appUUID: PathParam, teamID: PathParam) =>
  `/public/app/${appUUID}/team/${teamID}/lookup`;
export const API_LOOKUP_DATABASE_VARS = (databaseUUID: PathParam) =>
  `/public/database/${databaseUUID}/lookup`;

export const API_ASSIGN_USERS = (solutionID: PathParam) =>
  `solutions/${solutionID}/assign-users`;
export const API_UNASSIGN_USERS = (solutionID: PathParam) =>
  `solutions/${solutionID}/unassign-users`;

// Solution APIs
export const API_FETCH_SOLUTION_DASHBOARDS = (solutionVersionID: PathParam) =>
  `solution-version/${solutionVersionID}/dashboard`;
export const API_ADD_SOLUTION_DASHBOARD = (solutionVersionID: PathParam) =>
  `solution-version/${solutionVersionID}/dashboards`;
export const API_DELETE_SOLUTION_DASHBOARD = (
  solutionVersionID: PathParam,
  dashboardID: PathParam
) => `solution-version/${solutionVersionID}/dashboards/${dashboardID}`;
export const API_FETCH_SOLUTION_WIDGETS = (solutionVersionID: PathParam) =>
  `solution-version/${solutionVersionID}/widgets`;
export const API_FETCH_SOLUTION_APP_WIDGETS = (
  solutionVersionID: PathParam,
  appID: PathParam
) => `solution-version/${solutionVersionID}/app/${appID}/widgets`;
export const API_SOLUTION_WIDGETS_FETCH_FIELDS = (
  solutionVersionID: PathParam,
  appID: PathParam
) => `solution-version/${solutionVersionID}/app/${appID}/widget-fields`;
export const API_SOLUTION_WIDGET_PREVIEW = (
  solutionVersionID: PathParam,
  appID: PathParam
) => `solution-version/${solutionVersionID}/app/${appID}/widget-preview`;
export const API_CREATE_SOLUTION_WIDGET = (
  solutionVersionID: PathParam,
  appID: PathParam
) => `solution-version/${solutionVersionID}/app/${appID}/widget`;
export const API_COPY_SOLUTION_WIDGET = (
  solutionVersionID: PathParam,
  appID: PathParam,
  widgetID: PathParam
) =>
  `solution-version/${solutionVersionID}/app/${appID}/widgets-copy/${widgetID}`;
export const API_FETCH_SOLUTION_ACTION = (
  solutionVersionID: PathParam,
  appID: PathParam,
  actionID: PathParam
) => `solution-version/${solutionVersionID}/app/${appID}/action/${actionID}`;
export const API_UPDATE_SOLUTION_ACTION = (
  solutionVersionID: PathParam,
  appID: PathParam,
  actionID: PathParam
) => `solution-version/${solutionVersionID}/app/${appID}/action/${actionID}`;
export const API_PUBLISH_SOLUTION_VERSION = (
  solutionID: PathParam,
  solutionVersionID: PathParam
) => `solutions/${solutionID}/versions/${solutionVersionID}/publish`;
export const API_FETCH_SOLUTIONS_PERMISSION = () =>
  'solutions/permission-check';
export const API_GET_SOLUTION_TEMPLATES = () => `solution-templates`;
export const API_GET_SOLUTION_TEMPLATE_MEDIAS = (solutionTemplateId: string) =>
  `solution-template/${solutionTemplateId}/medias`;
export const API_IMPORT_SOLUTION = () => `solutions/import`;
export const API_CREATE_SOLUTION = () => `solutions`;
export const API_GET_SOLUTION = (solutionID: PathParam) =>
  `solutions/${solutionID}`;
export const API_VALIDATE_SOLUTION_NAME = () => `validate/solution-name`;

export const API_GET_ACTIVITY_FEEDS = (
  referenceType: string,
  referenceUUID: UUID
) => `activity-feed/${referenceType}/${referenceUUID}/`;

/** Returns interfaces.ts `Solution[]` */
export const API_FETCH_SOLUTIONS = '/solutions';

// Task Inbox
export const API_FETCH_EXPOSED_FIELDS = (
  solutionUUID: PathParam,
  versionUUID: PathParam,
  appID: PathParam
) => `solution/${solutionUUID}/version/${versionUUID}/app/${appID}/fields`;
export const API_GET_INBOX_VARIABLES = (
  solutionUUID: PathParam,
  versionUUID: PathParam,
  appID: PathParam
) => `solution/${solutionUUID}/version/${versionUUID}/app/${appID}/inbox`;
export const API_UPDATE_INBOX_VARIABLES = (
  solutionUUID: PathParam,
  versionUUID: PathParam,
  appID: PathParam
) => `solution/${solutionUUID}/version/${versionUUID}/app/${appID}/inbox`;

// Page type to Wiki page mapping. Undefined when no page available.
export const WIKI_URLS: Record<string, string | undefined> = {
  ROOT: 'https://support.checkbox.ai',
  DOCUSIGN: WIKI_SEARCH('Docusign'),
  [studioTypes.Start]: undefined,
  [studioTypes.Statement]: WIKI_SEARCH('Quick+Pages'),
  [studioTypes.YesNo]: WIKI_SEARCH('Quick+Pages'),
  [studioTypes.List]: WIKI_SEARCH('Quick+Pages'),
  [studioTypes.Text]: WIKI_SEARCH('Quick+Pages'),
  [studioTypes.Number]: WIKI_SEARCH('Quick+Pages'),
  [studioTypes.Dropdown]: WIKI_SEARCH('Quick+Pages'),
  [studioTypes.TableBuilder]: WIKI_SEARCH('Table'),
  [studioTypes.FormBuilder]: WIKI_SEARCH('Form'),
  [studioTypes.DocBuilder]: WIKI_SEARCH('Document+Generation'),
  [studioTypes.Comp]: WIKI_SEARCH('Computation'),
  [studioTypes.Merge]: WIKI_SEARCH('Merge'),
  [studioTypes.Filter]: WIKI_SEARCH('Form'),
  [studioTypes.Link]: WIKI_SEARCH('Routing'),
  [studioTypes.Loop]: WIKI_SEARCH('Loop'),
  [studioTypes.Workflow]: WIKI_SEARCH('Workflow'),
  [studioTypes.LoopIn]: undefined,
  [studioTypes.LoopOut]: undefined,
  [studioTypes.JavaScript]: WIKI_SEARCH('JavaScript'),
  [studioTypes.Email]: WIKI_SEARCH('Email'),
  [studioTypes.Docusign]: WIKI_SEARCH('E-Signature'),
  [studioTypes.Transcript]: WIKI_SEARCH('Transcript'),
  [studioTypes.DocReview]: WIKI_SEARCH('View+Document'),
  [studioTypes.End]: WIKI_SEARCH('End+Block'),
};

export const DEFAULT_EMPTY_SELECT_VALUE = {
  label: '',
  value: '',
};

// Zendesk widget constants

export const ZENDESK_ID = 'ze-snippet';
export const ZENDESK_URL =
  'https://static.zdassets.com/ekr/snippet.js?key=ee3f73a9-1359-4bfb-892b-4d14e296f282';

/* Assessments */
export const ASSESSMENT = {
  STATUS: {
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    SENT_BACK: 'Sent Back',
    PENDING_APPROVAL: 'Pending Approval',
    PENDING_ACCEPTANCE: 'Pending Acceptance',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  },
};

/* View Transcript Options */

export const DEFAULT_ERROR_MESSAGE =
  'There was an error processing your request, please contact support';

export const UNEDITABLE_VERSION_MESSAGE =
  'Changes cannot be made because this Solution version is uneditable.';

// Export code prefixes
export const APP_PREFIX = 'A';
export const SOLUTION_PREFIX = 'S';

export const SOLUTION_PREFIX_ERROR = `<b>ERROR:</b> Could not import solution, you have uploaded an import code for an app instead of a solution.
<br/>
You can find solution code under "Versions and Publishing", or by reaching out to the app code provider.`;

export const APP_PREFIX_ERROR = `<b>ERROR:</b> Could not import app, you have uploaded source code for a solution instead of an app.
<br/>
You can find app export code by clicking the relevant app on the Solutions page, or by reaching out to the original provider of the code.`;

export const DASHBOARD_CONFLICT_MSG = `This dashboard has been updated since you last accessed it.
                                      Please refresh to ensure you have the latest changes`;

export const INBOX_PATH = 'inbox';

export const NULL_UUID = '00000000-0000-0000-0000-000000000000';

export const ROUTE_BACK_METHOD = {
  DELETE_WIDGET_DATA_INCLUSIVE: 'deleteInclusive', // including the route back block
  DELETE_WIDGET_DATA_EXCLUSIVE: 'delete', // not including the route back block
  DELETE_ALL: 'deleteAll',
  KEEP: 'keep',
};

export const DATE_TIME_FORMATS = {
  'DD/MM/YYYY HH:mm': 'DD/MM/YYYY HH:mm',
  'MM/DD/YYYY HH:mm': 'MM/DD/YYYY HH:mm',
  'YYYY/MM/DD HH:mm': 'YYYY/MM/DD HH:mm',
  'D Month YYYY HH:mm': 'D MMMM YYYY HH:mm',
  'Month D, YYYY HH:mm': 'MMMM D, YYYY HH:mm',
  'D Mmm YYYY HH:mm': 'D MMM YYYY HH:mm',
  'Mmm D, YYYY HH:mm': 'MMM D, YYYY HH:mm',
};

export const WORKFLOW_COLUMNS = {
  HAS_APPROVED: 'HasApproved',
  HAS_REJECTED: 'HasRejected',
  HAS_SENT_BACK: 'HasSentBack',
  PENDING_RESPONSE: 'PendingResponse',
  APPROVAL_STATUS: 'ApprovalStatus',
};

export const EMBEDDED_LOGIN_SUCCESS_EVENT = 'EMBEDDED_LOGIN_SUCCESS';

// Google Drive Integration
export const API_GOOGLEDRIVE_EXCHANGE_COMPLETE = `public/integrations/googledrive/exchange/complete`;

// AIA Chat Properties
export const DEFAULT_CHAT_FONT_SIZE = 14;
